import { Component, Output, EventEmitter, OnInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { Professional } from '../../../../core/models/professional.model';
import { Subject, Subscription, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// PrimeNG Imports
import { DialogModule } from 'primeng/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Store } from '@ngrx/store';
import { SettingsService } from '../../../../core/services/settings.service';
import { selectCompanyId, selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { takeUntil } from 'rxjs/operators';
import { DatePicker } from 'primeng/datepicker';
import { MultiSelect } from 'primeng/multiselect';
import { Fluid } from 'primeng/fluid';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { toast } from 'ngx-sonner';

@Component({
    selector: 'app-new-shift-modal',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DialogModule,
        InputText,
        Select,
        DatePicker,
        MultiSelect,
        Fluid,
        AppButton,
        TranslateModule
    ],
    templateUrl: './new-shift-modal.html',
})
export class NewShiftModal implements OnInit, OnDestroy {
    @Output() close = new EventEmitter<void>();
    @Output() saved = new EventEmitter<void>();
    @Input() shift: any | null = null;

    doctors: Professional[] = [];
    selectedDoctorId: number | null = null;
    shiftTitle = '';
    startDate: Date | null = null;
    endDate: Date | null = null;

    showDaysDropdown = false;
    startTime = '09:00';
    endTime = '18:00';
    duration = 30;

    locations: any[] = [];
    selectedLocation: any | null = null;
    selectedDays: string[] = [];
    branchId: number | null = null;

    // Explicitly define days options for p-multiSelect
    daysOptions = [
        { label: 'Monday', value: 'Monday' },
        { label: 'Tuesday', value: 'Tuesday' },
        { label: 'Wednesday', value: 'Wednesday' },
        { label: 'Thursday', value: 'Thursday' },
        { label: 'Friday', value: 'Friday' },
        { label: 'Saturday', value: 'Saturday' },
        { label: 'Sunday', value: 'Sunday' }
    ];

    // Map Portuguese days from DB to English for UI
    private daysMapPtToEn: {[key: string]: string} = {
        'Segunda-feira': 'Monday',
        'Terça-feira': 'Tuesday',
        'Quarta-feira': 'Wednesday',
        'Quinta-feira': 'Thursday',
        'Sexta-feira': 'Friday',
        'Sábado': 'Saturday',
        'Domingo': 'Sunday'
    };

    // Map English days from UI to Portuguese for DB
    private daysMapEnToPt: {[key: string]: string} = {
        'Monday': 'Segunda-feira',
        'Tuesday': 'Terça-feira',
        'Wednesday': 'Quarta-feira',
        'Thursday': 'Quinta-feira',
        'Friday': 'Sexta-feira',
        'Saturday': 'Sábado',
        'Sunday': 'Domingo'
    };

    timeOptions: { label: string, value: string }[] = [];

    durationOptions = [
        { label: '15 minutes', value: 15 },
        { label: '30 minutes', value: 30 },
        { label: '45 minutes', value: 45 },
        { label: '60 minutes', value: 60 },
        { label: '90 minutes', value: 90 }
    ];
    selectedBranchId: number | null = null;

    days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    loading = false;
    fetching = false;
    error = '';

    doctorSearchText = '';
    showDoctorDropdown = false;
    private searchSubject = new Subject<string>();
    private searchSubscription: Subscription | undefined;
    private destroy$ = new Subject<void>();

    constructor(
        private professionalsService: ProfessionalsService,
        private cdr: ChangeDetectorRef,
        private translate: TranslateService,
        private store: Store,
        private settingsService: SettingsService
    ) { 
        this.generateTimeOptions();
    }

    async ngOnInit() {
        this.fetching = true;
        const timeoutId = setTimeout(() => {
            if (this.fetching) {
                this.fetching = false;
                this.cdr.detectChanges();
            }
        }, 10000); // 10 seconds timeout

        try {
            this.branchId = await firstValueFrom(this.store.select(selectBranchId));

            // Subscribe to search input changes with debounce
            this.searchSubscription = this.searchSubject.pipe(
                debounceTime(500), // 500ms delay
                distinctUntilChanged()
            ).subscribe(searchText => {
                this.doctorSearchText = searchText;
                this.loadDoctors();
            });

            // Load metadata first so it's ready for populateForm
            await Promise.all([
                this.loadDoctors(),
                this.loadLocations()
            ]);
            
            if (this.shift) {
                await this.fetchAndPopulate();
            }

            // Load Clinic Timings
            this.store.select(selectCompanyId).pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged()
            ).subscribe(id => {
                if (id) {
                    this.loadClinicTimings(id);
                }
            });
        } catch (error) {
            this.error = this.translate.instant('shift_load_failed');
        } finally {
            clearTimeout(timeoutId);
            this.fetching = false;
            this.cdr.detectChanges();
        }
    }

    private loadClinicTimings(companyId: number) {
        this.settingsService.getCompanyData(companyId).subscribe({
            next: (data: any) => {
                if (data && data.opening_time && data.closing_time) {
                    const opening = data.opening_time.substring(0, 5);
                    const closing = data.closing_time.substring(0, 5);

                    this.generateTimeOptions(opening, closing);

                    // Default a new shift's time range to the clinic's operating hours.
                    // Don't override times when editing an existing shift.
                    if (!this.shift) {
                        this.startTime = opening;
                        this.endTime = closing;
                    }

                    this.cdr.detectChanges();
                }
            },
            error: (err) => console.error('Error loading clinic timings:', err)
        });
    }

    generateTimeOptions(start: string = '08:00', end: string = '20:00') {
        this.timeOptions = [];
        const startHour = parseInt(start.split(':')[0]);
        const startMin = parseInt(start.split(':')[1]);
        const endHour = parseInt(end.split(':')[0]);
        const endMin = parseInt(end.split(':')[1]);

        for (let h = startHour; h <= endHour; h++) {
            for (let m = (h === startHour ? startMin : 0); m < 60; m += 30) {
                // If it's the last hour, check if minutes exceed defined end
                if (h === endHour && m > endMin) break;
                
                const hourStr = h.toString().padStart(2, '0');
                const minuteStr = m.toString().padStart(2, '0');
                const time = `${hourStr}:${minuteStr}`;
                this.timeOptions.push({ label: time, value: time });

                // If we exactly hit the end time, exit
                if (h === endHour && m === endMin) break;
            }
        }
    }

    async fetchAndPopulate() {
        if (!this.shift) return;
        const id = this.shift.id || this.shift.schedule_id;
        if (!id) {
            this.populateForm(this.shift);
            return;
        }

        try {
            const { data, error } = await this.professionalsService.getShiftById(id);
            if (data) {
                this.populateForm(data);
            } else {
                this.populateForm(this.shift);
            }
        } catch (e) {
            this.populateForm(this.shift);
        }
    }

    populateForm(shiftData: any) {
        if (!shiftData) return;

        this.selectedDoctorId = shiftData.doctor_id;
        const doctorName = shiftData.doctor_name || this.shift?.doctor_name || '';
        this.doctorSearchText = doctorName;

        // Ensure doctor is in the list for default display
        if (this.selectedDoctorId && doctorName) {
            const exists = this.doctors.find(d => d.doctor_id === this.selectedDoctorId);
            if (!exists) {
                this.doctors = [{
                     doctor_id: this.selectedDoctorId,
                     doctor_name: doctorName,
                     status: 'ativo',
                     branches_id: 0 // dummy
                } as any, ...this.doctors];
            }
        }

        this.shiftTitle = shiftData.title || shiftData.shift_title || '';
        
        // Format dates YYYY-MM-DD
        if (shiftData.start_date) {
            this.startDate = new Date(shiftData.start_date);
        }
        if (shiftData.end_date) {
            this.endDate = new Date(shiftData.end_date);
        }

        const sanitizeTime = (t: string) => {
            if (!t) return null;
            const parts = t.split(':');
            return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
        };

        this.startTime = shiftData.start_time ? sanitizeTime(shiftData.start_time) || '09:00' : '09:00';
        this.endTime = shiftData.end_time ? sanitizeTime(shiftData.end_time) || '18:00' : '18:00';
        this.duration = shiftData.duration || 30;

        // Populate Location
        const officeId = shiftData.office_id || shiftData.id_office;
        const branchId = shiftData.branches_id || shiftData.id_branch;
        
        if (officeId || branchId) {
             const loc = this.locations.find(l => 
                (officeId && l.id === officeId) || 
                (branchId && l.branches_id === branchId && !officeId)
             );
             if (loc) {
                 this.selectedLocation = loc;
             }
        }

        // Populate Week Days (day_of_week)
        const weekDays = shiftData.day_of_week || shiftData.week_days;
        if (weekDays) {
            let daysArray: string[] = [];
            if (Array.isArray(weekDays)) {
                daysArray = weekDays;
            } else if (typeof weekDays === 'string') {
                try {
                    // Handle Postgres array literal {Monday,Tuesday}
                    let cleaned = weekDays.replace(/^{/, '[').replace(/}$/, ']');
                     if (!cleaned.includes('"') && !cleaned.includes("'")) {
                         const parts = cleaned.replace('[', '').replace(']', '').split(',').map((s: string) => `"${s.trim()}"`);
                         cleaned = `[${parts.join(',')}]`;
                     }
                    daysArray = JSON.parse(cleaned);
                } catch (e) {
                     daysArray = weekDays.replace('{','').replace('}','').split(',').map((d: string) => d.trim());
                }
            }

            // Convert PT days to EN if needed
            this.selectedDays = daysArray.map(d => this.daysMapPtToEn[d] || d);
        }
    }

    ngOnDestroy() {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    async loadDoctors() {
        // Load doctors for dropdown with search (limit 1000)
        // If query is empty, it loads all active doctors
        const { data } = await this.professionalsService.searchDoctors(
            null, // we can pass selectedBranchId if we want to filter by location
            this.doctorSearchText,
            1000,
            0
        );

        if (data) {
            // Map id to doctor_id and name to doctor_name to match Professional interface
            this.doctors = data.map((d: any) => ({
                ...d,
                doctor_id: d.id,
                doctor_name: d.name
            })) as any[];

            // If editing and selectedDoctorId is set, ensure it's in the list or loaded
            if (this.selectedDoctorId && !this.doctors.find(d => d.doctor_id === this.selectedDoctorId)) {
                // Ideally create a partial doctor object so the dropdown shows the name
                // existing logic handles this via doctorSearchText display
            }
        }
    }

    onDoctorSearchInput(event: any) {
        // Debounced search
        this.showDoctorDropdown = true;
        this.searchSubject.next(event.target.value);
    }

    onDoctorFocus() {
        this.showDoctorDropdown = true;
        if (this.doctors.length === 0) {
            this.loadDoctors();
        }
    }

    selectDoctor(doctor: any) {
        this.selectedDoctorId = doctor.doctor_id;
        this.doctorSearchText = doctor.doctor_name;
        this.showDoctorDropdown = false;
    }

    // click outside handler would be nice but for now simpler
    closeDoctorDropdown() {
        // timeout to allow click event to register
        setTimeout(() => {
            this.showDoctorDropdown = false;
        }, 200);
    }

    async loadLocations() {
        // Load locations/offices
        const { data } = await this.professionalsService.getOffices();
        if (data) {
            this.locations = data;
            
            // If editing, find and set selectedLocation
            const targetId = this.shift?.office_id || this.shift?.branches_id || this.shift?.id;
            if (targetId) {
                this.selectedLocation = this.locations.find(l => l.id === targetId || l.office_id === targetId);
            }
        }
    }

    toggleDay(day: string) {
        const index = this.selectedDays.indexOf(day);
        if (index === -1) {
            this.selectedDays.push(day);
        } else {
            this.selectedDays.splice(index, 1);
        }
    }

    onClose() {
        this.close.emit();
    }

    async onSave() {
        this.error = '';
        if (!this.selectedDoctorId || !this.shiftTitle || this.selectedDays.length === 0 || !this.startDate || !this.selectedLocation) {
            this.error = this.translate.instant('FILL_ALL_FIELDS');
            return;
        }

        this.loading = true;

        // Validation: End date cannot be smaller than start date
        if (this.startDate && this.endDate && this.endDate < this.startDate) {
            this.error = this.translate.instant('error_invalid_date_range');
            this.loading = false;
            return;
        }

        // Validation: End time must be greater than start time
        if (this.startTime && this.endTime) {
            const start = this.startTime.split(':').map(Number);
            const end = this.endTime.split(':').map(Number);
            const startVal = start[0] * 60 + start[1];
            const endVal = end[0] * 60 + end[1];
            
            if (endVal <= startVal) {
                this.error = this.translate.instant('error_invalid_time_range');
                this.loading = false;
                return;
            }
        }

        try {
            // Convert selected days to Portuguese for DB
            const dbDays = this.selectedDays.map(d => this.daysMapEnToPt[d] || d);

            const shiftData = {
                doctor_id: this.selectedDoctorId,
                title: this.shiftTitle,
                start_date: this.startDate ? this.startDate.toISOString().split('T')[0] : null,
                end_date: this.endDate ? this.endDate.toISOString().split('T')[0] : null,
                start_time: this.startTime,
                end_time: this.endTime,
                duration: this.duration,
                office_id: this.selectedLocation ? this.selectedLocation.id : null,
                branches_id: this.branchId,
                day_of_week: dbDays
            };

            let result;
            const shiftId = this.shift?.id || this.shift?.schedule_id;
            if (this.shift && shiftId) {
                 result = await this.professionalsService.updateShift(shiftId, shiftData);
            } else {
                 result = await this.professionalsService.createShift(shiftData);
            }

            const { error } = result;

            if (error) {
                this.error = this.translate.instant('shift_save_failed');
                toast.error(this.translate.instant('error_update'));
                this.loading = false;
                return;
            }

            toast.success(this.shift ? this.translate.instant('success_update') : this.translate.instant('professionals_shift_created_success'));
            this.saved.emit();
        } catch (err) {
            this.error = this.translate.instant('unexpected_error');
            toast.error(this.translate.instant('error_update'));
            this.loading = false;
        }
    }
}
